import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "convex/react";
import { api } from "@packages/backend/convex/_generated/api";
import {
  Filter,
  ExternalLink,
  ArrowRight,
  Users,
  Calendar,
  MapPin,
} from "lucide-react";

import { PageHero } from "../components/PageHero";

interface Project {
  _id: string;
  title: string;
  description: string;
  bannerUrl: string;
  websiteUrl?: string;
  isActive: boolean;
  isBPO?: boolean;
  isFeatured?: boolean;
  category?: string;
  clientName?: string;
  completedDate?: string;
  technologies?: string[];
  displayOrder?: number;
}

export const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const projectsData = useQuery(
    api.marketplace.jobs.completedProjects.listActive,
  );
  const loading = projectsData === undefined;
  const error = null; // Convex handles errors through it's own mechanism, keeping null for UI compatibility

  const sortedProjects = useMemo(() => {
    if (!projectsData) return [];

    return [...projectsData].sort((a, b) => {
      // BPO projects first
      if (a.isBPO && !b.isBPO) return -1;
      if (!a.isBPO && b.isBPO) return 1;

      // Then by displayOrder
      const orderA = a.displayOrder ?? 0;
      const orderB = b.displayOrder ?? 0;
      return orderA - orderB;
    });
  }, [projectsData]);

  const categories = useMemo(() => {
    if (!sortedProjects) return [];

    const categoryCounts = sortedProjects.reduce(
      (acc, project) => {
        const cat = project.category || "uncategorized";
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const dynamicCategories = Object.entries(categoryCounts).map(
      ([id, count]) => ({
        id,
        name: id
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        count,
      }),
    );

    dynamicCategories.unshift({
      id: "all",
      name: "All Projects",
      count: sortedProjects.length,
    });

    return dynamicCategories;
  }, [sortedProjects]);

  const filteredProjects = useMemo(() => {
    return activeFilter === "all"
      ? sortedProjects
      : sortedProjects.filter(
          (project) => (project.category || "uncategorized") === activeFilter,
        );
  }, [sortedProjects, activeFilter]);

  return (
    <div>
      <PageHero
        title="African Talent, Global Impact"
        subtitle="See how Africa's most talented professionals are delivering world-class results for global clients. Every project showcases our commitment to honoring talent, creating value, and building lasting relationships."
      />

      {/* Featured Projects Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-brand-grey mb-8 flex items-center">
            <span className="w-2 h-8 bg-brand-primary mr-3 rounded-full"></span>
            Featured Project
          </h2>

          <div className="bg-gradient-to-br from-brand-secondary/30 to-white border border-brand-secondary rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative h-64 lg:h-auto">
                <img
                  src="/images/bpo/data-processing.jpeg"
                  alt="MVP Data Analyst Skills Training"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-brand-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                  Education & Training
                </div>
              </div>
              <div className="p-8 lg:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-4">
                  <span className="flex items-center text-sm text-brand-grey/80 bg-white px-3 py-1 rounded-full border border-gray-100">
                    <MapPin className="w-3 h-3 mr-1 text-brand-primary" />{" "}
                    Rwanda
                  </span>
                  <span className="flex items-center text-sm text-brand-grey/80 bg-white px-3 py-1 rounded-full border border-gray-100">
                    <Users className="w-3 h-3 mr-1 text-brand-primary" /> 20
                    Participants
                  </span>
                </div>

                <h3 className="text-2xl md:text-3xl font-bold text-brand-grey mb-4 font-playfair">
                  MVP Data Analyst Skills Training & Job Placement
                </h3>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  A pioneering German-Rwanda-Uganda collaboration designed to
                  bridge the gap between European companies' IT talent needs and
                  Africa's skilled workforce potential.
                </p>

                <div className="flex flex-wrap gap-2 mb-8">
                  {["Data Analytics", "Machine Learning", "BPO"].map(
                    (tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-white border border-brand-primary/20 text-brand-primary text-xs rounded-full font-medium"
                      >
                        {tag}
                      </span>
                    ),
                  )}
                </div>

                <div>
                  <Link
                    to="/projects/rwanda-training-2025"
                    className="inline-flex items-center text-brand-primary font-semibold hover:text-brand-tertiary transition-colors group"
                  >
                    View Project Details
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-center">
            <div className="flex items-center mb-6 lg:mb-0">
              <Filter className="w-5 h-5 text-brand-grey mr-3" />
              <span className="text-brand-grey font-semibold">
                Filter by Service:
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveFilter(category.id)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 transform hover:-translate-y-1 ${
                    activeFilter === category.id
                      ? "bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg"
                      : "bg-white border border-gray-200 text-gray-600 hover:bg-brand-secondary hover:text-brand-primary shadow-sm"
                  }`}
                >
                  {category.name}
                  <span
                    className={`ml-2 text-xs px-2 py-1 rounded-full ${
                      activeFilter === category.id
                        ? "bg-white/20"
                        : "bg-black/10 text-gray-600"
                    }`}
                  >
                    {category.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {loading && (
        <div className="text-center py-16">
          <p className="text-xl text-brand-grey">Loading projects...</p>
        </div>
      )}
      {error && (
        <div className="text-center py-16">
          <p className="text-xl text-red-500">Error: {error}</p>
        </div>
      )}

      {/* Projects Grid */}
      {!loading && !error && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <div
                  key={project._id}
                  className="bg-white border border-gray-200 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 group animate-fadeInUp cursor-pointer"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="p-4">
                    <div className="relative overflow-hidden rounded-2xl">
                      <img
                        src={project.bannerUrl}
                        alt={project.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <p className="text-sm font-medium">
                          Click to view details
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 pb-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="px-3 py-1 rounded-full text-xs font-medium border bg-brand-secondary border-gray-200 text-brand-grey">
                        {project.category
                          ? project.category
                              .split("-")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1),
                              )
                              .join(" ")
                          : "General"}
                      </span>
                      {project.websiteUrl ? (
                        <a
                          href={project.websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-brand-primary transition-colors" />
                        </a>
                      ) : (
                        <div />
                      )}
                    </div>

                    <h3 className="text-xl font-bold text-brand-grey mb-2 group-hover:text-brand-primary transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.technologies
                        ?.slice(0, 3)
                        .map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg border border-gray-200"
                          >
                            {tag}
                          </span>
                        ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-500">
                        <Users className="w-4 h-4 mr-1" />
                        {project.clientName || "Confidential Client"}
                      </div>
                      {project.completedDate && (
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(project.completedDate).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredProjects.length === 0 && (
              <div className="text-center py-16">
                <p className="text-xl text-gray-600 mb-4">
                  No projects found in this category.
                </p>
                <button
                  onClick={() => setActiveFilter("all")}
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  View All Projects
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="relative">
              <img
                src={selectedProject.bannerUrl}
                alt={selectedProject.title}
                className="w-full h-64 object-cover rounded-t-3xl"
              />
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center text-brand-grey hover:bg-white transition-colors shadow-lg"
              >
                ×
              </button>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <h2 className="text-3xl font-bold text-brand-grey mb-4">
                    {selectedProject.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {selectedProject.description}
                  </p>

                  {selectedProject.technologies &&
                    selectedProject.technologies.length > 0 && (
                      <div className="mb-8">
                        <h3 className="text-xl font-bold text-brand-grey mb-4">
                          Technologies Used
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedProject.technologies.map((tech, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-brand-secondary border border-brand-primary/20 text-brand-primary rounded-full text-sm font-medium"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                </div>

                <div>
                  <div className="bg-brand-secondary border border-brand-primary/20 rounded-2xl p-6 mb-6">
                    <h3 className="text-lg font-bold text-brand-grey mb-4">
                      Project Details
                    </h3>
                    <div className="space-y-4">
                      {selectedProject.clientName && (
                        <div>
                          <span className="text-sm text-gray-500">Client</span>
                          <p className="font-semibold text-brand-grey">
                            {selectedProject.clientName}
                          </p>
                        </div>
                      )}
                      {selectedProject.completedDate && (
                        <div>
                          <span className="text-sm text-gray-500">
                            Completed
                          </span>
                          <p className="font-semibold text-brand-grey">
                            {new Date(
                              selectedProject.completedDate,
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {selectedProject.websiteUrl && (
                        <div>
                          <span className="text-sm text-gray-500">Website</span>
                          <a
                            href={selectedProject.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-brand-primary hover:underline"
                          >
                            View Project{" "}
                            <ExternalLink className="inline-block w-4 h-4 ml-1" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  Start Your Similar Project
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="/images/cta-1.jpeg"
            alt="African professionals collaborating"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/90 via-brand-tertiary/85 to-brand-primary/90"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-float"></div>
        <div
          className="absolute bottom-10 right-10 w-16 h-16 bg-white/10 rounded-full animate-float"
          style={{ animationDelay: "1s" }}
        ></div>
        <div
          className="absolute top-1/2 left-1/4 w-12 h-12 bg-white/10 rounded-full animate-float"
          style={{ animationDelay: "2s" }}
        ></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="animate-fadeInUp">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Be Our Next Success Story?
            </h2>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Join global clients who've experienced the power of African
              excellence. Let's create your next breakthrough together.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/contact"
                className="bg-white text-green-600 px-8 py-4 rounded-2xl text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                Start Your Project
              </Link>
              <Link
                to="/services"
                className="border-2 border-white text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white hover:text-green-600 transition-all duration-300 transform hover:-translate-y-1"
              >
                Explore Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
